import React, { useCallback, useMemo, useState } from 'react';
import { Upload, FileText, CheckCircle2, SearchCheck } from 'lucide-react';
import {
  prepareImportFromCSV,
  importPreviewRows,
  checkPreviewDuplicatesInDb,
  ImportPreviewRow,
  ImportResult,
} from '../lib/services/ImportService';
import { BankId, BANK_LABELS, decodeCsvBytes } from '../lib/import/bankParsers';
import { useCategories, useAccounts } from '../hooks';

type ForcedBank = BankId | 'auto';

const BANK_OPTIONS: { id: ForcedBank; label: string }[] = [
  { id: 'auto', label: 'Auto-détection' },
  { id: 'boursobank', label: 'BoursoBank' },
  { id: 'credit_agricole', label: 'Crédit Agricole' },
  { id: 'fortuneo', label: 'Fortuneo' },
  { id: 'sumeria', label: 'Sumeria' },
  { id: 'bnp', label: 'BNP' },
  { id: 'lcl', label: 'LCL' },
  { id: 'sg', label: 'Société Générale' },
  { id: 'suivifinance', label: 'SuiviFinance' },
];

const PREVIEW_LIMIT = 100;

const ImportPage: React.FC = () => {
  const { categories } = useCategories();
  const { comptes } = useAccounts();
  const [csvContent, setCsvContent] = useState('');
  const [forcedBank, setForcedBank] = useState<ForcedBank>('auto');
  const [detectedBank, setDetectedBank] = useState<string | null>(null);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<ImportPreviewRow[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [bulkCompteNom, setBulkCompteNom] = useState('');
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [duplicateCheckMsg, setDuplicateCheckMsg] = useState<string | null>(null);

  const defaultCompteNom = useMemo(() => {
    const d = comptes.find((c) => c.is_default) || comptes.find((c) => c.actif !== false) || comptes[0];
    return d?.nom || '';
  }, [comptes]);

  const validCount = useMemo(
    () => previewRows.filter((r) => !r.skip && !r.error).length,
    [previewRows],
  );

  const duplicateCount = useMemo(
    () => previewRows.filter((r) => r.duplicateDb).length,
    [previewRows],
  );

  const categoriesByType = useCallback(
    (type: ImportPreviewRow['type']) => {
      const want = type === 'transfert' ? 'depense' : type;
      return categories
        .filter((c) => c.type === want)
        .slice()
        .sort((a, b) => a.nom.localeCompare(b.nom, 'fr', { sensitivity: 'base' }));
    },
    [categories],
  );

  const buildPreview = useCallback((content: string, bank: ForcedBank) => {
    if (!content.trim()) {
      setPreviewRows([]);
      setDetectedBank(null);
      setParseErrors([]);
      setShowPreview(false);
      return;
    }
    const { parsed, rows } = prepareImportFromCSV(content, bank);
    setDetectedBank(parsed.bankLabel);
    setParseErrors(parsed.errors);
    setPreviewRows(rows);
    setShowPreview(true);
    setImportResult(null);
    setDuplicateCheckMsg(null);
  }, []);

  const onSelectBank = (bank: ForcedBank) => {
    setForcedBank(bank);
    if (csvContent.trim()) buildPreview(csvContent, bank);
  };

  const onPasteChange = (text: string) => {
    setCsvContent(text);
    setFileName(null);
    if (text.trim().length > 20) buildPreview(text, forcedBank);
    else setShowPreview(false);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const buf = reader.result;
      if (!(buf instanceof ArrayBuffer)) return;
      const text = decodeCsvBytes(buf);
      setCsvContent(text);
      buildPreview(text, forcedBank);
    };
    // ArrayBuffer pour détecter Windows-1252 (exports Fortuneo)
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const updateRow = (key: string, patch: Partial<ImportPreviewRow>) => {
    setPreviewRows((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        const next = { ...r, ...patch };
        if (patch.type && patch.montant == null) {
          const abs = Math.abs(next.montant);
          next.montant = patch.type === 'revenu' ? abs : -abs;
        }
        // Si le type change, garder la catégorie seulement si elle existe encore pour ce type
        if (patch.type) {
          const allowed = categoriesByType(patch.type).map((c) => c.nom);
          if (next.categorieNom && !allowed.includes(next.categorieNom)) {
            next.categorieNom = '';
          }
        }
        return next;
      }),
    );
  };

  const applyCompteToAll = (compteNom: string) => {
    setBulkCompteNom(compteNom);
    setPreviewRows((prev) => prev.map((r) => ({ ...r, compteNom })));
  };

  const handleCheckDuplicates = async () => {
    if (!previewRows.length) return;
    setCheckingDuplicates(true);
    setDuplicateCheckMsg(null);
    try {
      const result = await checkPreviewDuplicatesInDb(previewRows);
      setPreviewRows(result.rows);
      if (result.error) {
        setDuplicateCheckMsg(`Erreur : ${result.error}`);
      } else if (result.matched === 0) {
        setDuplicateCheckMsg(
          `Aucune correspondance date+montant sur ${result.checked} ligne(s) vérifiée(s).`,
        );
      } else {
        setDuplicateCheckMsg(
          `${result.matched} doublon(s) trouvé(s) (même date + montant) — marqué(s) à ignorer.`,
        );
      }
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const handleImport = async () => {
    if (!previewRows.length) return;
    setLoading(true);
    try {
      const result = await importPreviewRows(previewRows);
      setImportResult(result);
    } finally {
      setLoading(false);
    }
  };

  const displayRows = previewRows.slice(0, PREVIEW_LIMIT);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Importer des transactions</h1>
        <p className="mt-1 text-sm text-dark-400">
          CSV SuiviFinance ou relevé bancaire (BoursoBank, CA, Fortuneo, Sumeria,
          BNP, LCL, SG). Prévisualisez avant d&apos;importer.
        </p>
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-dark-900/60 p-5 space-y-4">
        <div>
          <label className="mb-2 block text-sm font-medium text-dark-300">Banque</label>
          <div className="flex flex-wrap gap-2">
            {BANK_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => onSelectBank(opt.id)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  forcedBank === opt.id
                    ? 'bg-primary-500/20 text-primary-400 ring-1 ring-primary-500/40'
                    : 'bg-dark-800 text-dark-400 hover:text-white'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {detectedBank && showPreview && (
          <div className="flex items-center gap-2 text-sm text-primary-400">
            <CheckCircle2 className="h-4 w-4" />
            Détecté : {detectedBank}
            {forcedBank !== 'auto' ? ' (forcé)' : ''} ✓
          </div>
        )}

        <div>
          <label className="mb-2 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-white/10 bg-dark-800/50 px-4 py-8 text-sm text-dark-300 transition hover:border-primary-500/40 hover:text-white">
            <Upload className="h-5 w-5" />
            <span>
              {fileName ? `Fichier : ${fileName}` : 'Choisir un fichier CSV'}
            </span>
            <input
              type="file"
              accept=".csv,.txt,text/csv,text/plain"
              className="hidden"
              onChange={onFileChange}
            />
          </label>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-dark-300">
            Ou collez le CSV
          </label>
          <textarea
            value={csvContent}
            onChange={(e) => onPasteChange(e.target.value)}
            rows={6}
            placeholder="Date,Description,Montant,Type,Catégorie,Compte…"
            className="w-full rounded-xl border border-white/[0.06] bg-dark-950 px-3 py-2 font-mono text-sm text-white placeholder:text-dark-600 focus:border-primary-500/40 focus:outline-none"
          />
        </div>

        {!showPreview && (
          <button
            type="button"
            disabled={!csvContent.trim()}
            onClick={() => buildPreview(csvContent, forcedBank)}
            className="inline-flex items-center gap-2 rounded-xl bg-primary-500 px-4 py-2.5 text-sm font-semibold text-dark-950 disabled:opacity-40"
          >
            <FileText className="h-4 w-4" />
            Prévisualiser
          </button>
        )}
      </div>

      {showPreview && (
        <div className="space-y-4 rounded-2xl border border-white/[0.06] bg-dark-900/60 p-5">
          <h2 className="text-lg font-semibold text-white">
            Prévisualisation ({validCount} ligne{validCount > 1 ? 's' : ''}
            {previewRows.length > PREVIEW_LIMIT
              ? ` — ${PREVIEW_LIMIT} premières affichées`
              : ''}
            )
          </h2>

          {parseErrors.length > 0 && (
            <ul className="space-y-1 rounded-lg bg-red-950/40 px-3 py-2 text-xs text-red-300">
              {parseErrors.slice(0, 12).map((err, i) => (
                <li key={i}>• {err}</li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-white/[0.06] bg-dark-950/50 px-3 py-2.5">
            <label className="text-xs font-medium text-dark-300" htmlFor="bulk-compte">
              Compte pour toutes les lignes
            </label>
            <select
              id="bulk-compte"
              value={bulkCompteNom}
              onChange={(e) => applyCompteToAll(e.target.value)}
              className="min-w-[12rem] rounded-md border border-white/10 bg-dark-900 px-2 py-1.5 text-sm text-white"
            >
              <option value="">
                {defaultCompteNom
                  ? `défaut (${defaultCompteNom})`
                  : 'défaut (1er compte)'}
              </option>
              {comptes.map((c) => (
                <option key={c.id} value={c.nom}>
                  {c.nom}
                  {c.is_default ? ' ★' : ''}
                </option>
              ))}
            </select>
            <span className="text-[11px] text-dark-500">
              Applique le même compte à toute la prévisualisation (modifiable ligne par ligne
              ensuite).
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleCheckDuplicates}
              disabled={checkingDuplicates || previewRows.length === 0}
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-dark-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-dark-700 disabled:opacity-40"
              title="Compare date + montant avec les transactions déjà en base"
            >
              <SearchCheck className="h-4 w-4" />
              {checkingDuplicates ? 'Vérification…' : 'Vérifier les doublons en base'}
            </button>
            {duplicateCount > 0 && (
              <span className="text-xs text-amber-300">
                {duplicateCount} ligne{duplicateCount > 1 ? 's' : ''} déjà en base (ignorée
                {duplicateCount > 1 ? 's' : ''} à l&apos;import)
              </span>
            )}
          </div>
          {duplicateCheckMsg && (
            <p className="text-xs text-dark-300">{duplicateCheckMsg}</p>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-dark-500">
                  <th className="px-2 py-2" title="Ignorer à l'import">
                    Ign.
                  </th>
                  <th className="px-2 py-2">Date</th>
                  <th className="px-2 py-2">Description</th>
                  <th className="px-2 py-2">Montant</th>
                  <th className="px-2 py-2">Type</th>
                  <th className="px-2 py-2">Catégorie</th>
                  <th className="px-2 py-2">Compte</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row) => (
                  <tr
                    key={row.key}
                    className={`border-b border-white/[0.04] ${
                      row.error
                        ? 'bg-red-950/30'
                        : row.duplicateDb || row.skip
                          ? 'bg-amber-950/30 opacity-70'
                          : row.warning
                            ? 'bg-amber-950/20'
                            : ''
                    }`}
                  >
                    <td className="px-2 py-1.5">
                      <input
                        type="checkbox"
                        checked={row.skip}
                        onChange={(e) => updateRow(row.key, { skip: e.target.checked })}
                        title={
                          row.duplicateDb
                            ? 'Doublon détecté en base — décochez pour importer quand même'
                            : 'Ignorer cette ligne à l’import'
                        }
                        className="rounded border-dark-600 bg-dark-900 text-primary-500 focus:ring-primary-500"
                      />
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-dark-200">{row.date}</td>
                    <td className="max-w-[220px] truncate px-2 py-1.5 text-white" title={row.warning || row.description}>
                      {row.description}
                      {row.duplicateDb && (
                        <span className="ml-1 text-[10px] font-semibold uppercase text-amber-400">
                          doublon
                        </span>
                      )}
                    </td>
                    <td
                      className={`whitespace-nowrap px-2 py-1.5 tabular-nums ${
                        row.montant < 0 ? 'text-red-300' : 'text-emerald-300'
                      }`}
                    >
                      {row.montant.toFixed(2)}
                    </td>
                    <td className="px-2 py-1.5">
                      <select
                        value={row.type}
                        onChange={(e) =>
                          updateRow(row.key, {
                            type: e.target.value as ImportPreviewRow['type'],
                          })
                        }
                        className="rounded-md border border-white/10 bg-dark-950 px-1.5 py-1 text-xs text-white"
                      >
                        <option value="depense">depense</option>
                        <option value="revenu">revenu</option>
                        <option value="transfert">transfert</option>
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      <select
                        value={row.categorieNom}
                        onChange={(e) =>
                          updateRow(row.key, { categorieNom: e.target.value })
                        }
                        className="min-w-[9rem] max-w-[14rem] rounded-md border border-white/10 bg-dark-950 px-1.5 py-1 text-xs text-white"
                        title="Choisir une catégorie"
                      >
                        <option value="">—</option>
                        {categoriesByType(row.type).map((c) => (
                          <option key={c.id} value={c.nom}>
                            {c.nom}
                          </option>
                        ))}
                        {row.categorieNom &&
                          !categoriesByType(row.type).some((c) => c.nom === row.categorieNom) && (
                            <option value={row.categorieNom}>
                              {row.categorieNom} (suggéré)
                            </option>
                          )}
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      <select
                        value={row.compteNom}
                        onChange={(e) =>
                          updateRow(row.key, { compteNom: e.target.value })
                        }
                        className="min-w-[8rem] max-w-[12rem] rounded-md border border-white/10 bg-dark-950 px-1.5 py-1 text-xs text-white"
                        title="Choisir un compte"
                      >
                        <option value="">
                          {defaultCompteNom
                            ? `défaut (${defaultCompteNom})`
                            : 'défaut'}
                        </option>
                        {comptes.map((c) => (
                          <option key={c.id} value={c.nom}>
                            {c.nom}
                            {c.is_default ? ' ★' : ''}
                          </option>
                        ))}
                        {row.compteNom &&
                          !comptes.some((c) => c.nom === row.compteNom) && (
                            <option value={row.compteNom}>{row.compteNom}</option>
                          )}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => {
                setShowPreview(false);
                setImportResult(null);
              }}
              className="rounded-xl border border-white/10 px-4 py-2.5 text-sm font-medium text-dark-300 hover:text-white"
            >
              Annuler
            </button>
            <button
              type="button"
              disabled={loading || validCount === 0}
              onClick={handleImport}
              className="inline-flex items-center gap-2 rounded-xl bg-primary-500 px-4 py-2.5 text-sm font-semibold text-dark-950 disabled:opacity-40"
            >
              {loading
                ? 'Import…'
                : `Importer ${validCount} transaction${validCount > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}

      {importResult && (
        <div
          className={`rounded-xl px-4 py-3 text-sm ${
            importResult.success
              ? 'bg-emerald-950/40 text-emerald-300'
              : 'bg-red-950/40 text-red-300'
          }`}
        >
          {importResult.importedCount} importée(s), {importResult.duplicatesSkipped}{' '}
          doublon(s) ignoré(s)
          {importResult.errors.length > 0
            ? ` — ${importResult.errors.length} erreur(s)`
            : ''}
          {importResult.errors[0] && (
            <div className="mt-1 text-xs opacity-80">{importResult.errors[0].message}</div>
          )}
        </div>
      )}

      <p className="text-xs text-dark-500">
        Formats : {Object.values(BANK_LABELS).filter((l) => l !== 'Format inconnu').join(', ')}.
        Dépenses en négatif, revenus en positif. Utilisez « Vérifier les doublons en base »
        (date + montant) avant d&apos;importer.
      </p>
    </div>
  );
};

export default ImportPage;
